import { useCallback, useMemo } from 'react';
import { drawerWidth, groupBy } from '../utils/Common';
import { Menu, MenuItem } from '../utils/Menu';
import { useNavigate } from "react-router-dom";
import { StateContextPropertyType, useStateContext } from '../reducers';
import { Box, Collapse, Divider, Drawer, IconButton, List, ListItem, ListItemButton, SxProps, Theme, Toolbar, Typography, useMediaQuery, useTheme } from '@mui/material';
import { KeyboardDoubleArrowLeft as KeyboardDoubleArrowLeftIcon } from '@mui/icons-material';

function Sidebar() {
    const {authState, menuState, setMenuState}: StateContextPropertyType = useStateContext();
    const navigate = useNavigate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    const groupedMenu = useMemo(() =>
        groupBy(Menu.filter((m) => (authState?.daftar_menu?.length && authState.daftar_menu.filter(am => m.children.filter(c => c.code === am.kode_menu).length > 0).length > 0) || m.groupName === "Dashboard" || m.groupName === "Profil"), "groupName")
        // groupBy(Menu, "groupName")
    , [authState, Menu]);

    const getSelectedGroup = useCallback((param: MenuItem) => {
        if (!groupedMenu?.length || !menuState)
            return;

        for (let i = 0; i < groupedMenu.length; i++)
        {
            if (groupedMenu[i].key === param.groupName)
            {
                setMenuState({...menuState, type: "menuGroup", selectedMenuGroup: param.groupName === menuState.selectedMenuGroup?.groupName ? null : param});
                return;
            }
        }

        setMenuState({...menuState, type: "menuGroup", selectedMenuGroup: undefined});
    }, [menuState, groupedMenu]);

    const getSelectedMenu = useCallback((param: MenuItem) => {
        if (!groupedMenu?.length || !menuState)
            return;

        for (let i = 0; i < groupedMenu.length; i++)
        {
            if (groupedMenu[i].value.filter(v => v.id === param.id)?.length)
            {
                setMenuState({...menuState, type: "menu", selectedMenu: param});
                return;
            }
        }

        setMenuState({...menuState, type: "menu", selectedMenu: undefined});
    }, [menuState, groupedMenu]);

    // useEffect(() => console.log({menuState}), [menuState]);

    const toolbar = useMemo(() => {
        return (
            <>
                <Toolbar
                    sx={{
                        display: "flex",
                        flexDirection: "row",
                        justifyContent: "space-between",
                    }}
                    className="text-color-white"
                >
                    <Typography variant="h6" noWrap component="div">
                        Menu
                    </Typography>
                </Toolbar>
                <Divider />
            </>
        );
    }, []);

    const mobileToolbar = useMemo(() => {
        return (
            <>
                <Toolbar
                    sx={{
                        display: "flex",
                        flexDirection: "row",
                        justifyContent: "space-between",
                    }}
                    className="text-color-white"
                >
                    <Typography variant="h6" noWrap component="div">
                        Menu
                    </Typography>
                    <IconButton
                        color="inherit"
                        aria-label="Buka Menu"
                        edge="start"
                        onClick={() => {
                            setMenuState({...menuState, type: "toggle", showSidebar: !menuState?.showSidebar});
                        }}
                    >
                        <KeyboardDoubleArrowLeftIcon />
                    </IconButton>
                </Toolbar>
                <Divider />
            </>
        );
    }, [menuState, setMenuState]);

    const drawerList = useCallback((isMobile = false) => {
        const itemStyle: SxProps<Theme> = {
            display: "flex",
            flexDirection: "column",
            width: "100%",
        };
        const itemSelectedStyle: SxProps<Theme> = {
            fontWeight: "bolder",
            borderBottom: "2px solid rgba(255, 255, 255, 0.5)",
        };
        const itemButtonStyle: SxProps<Theme> = {
            width: "100%",
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            justifyContent: "start",
        };
        const itemButtonSubmenuStyle: SxProps<Theme> = {
            paddingLeft: "2.2rem",
            ...itemButtonStyle,
        };
        const subItemContainerStyle: SxProps<Theme> = {
            display: "flex",
            flexDirection: "column",
            width: "100%",
        };
        const itemIconStyle: SxProps<Theme> = {
            marginRight: "5px",
            color: "rgba(255, 255, 255, 0.5)",
        };
        const itemIconSelectedStyle: SxProps<Theme> = {
            color: "rgba(255, 255, 255, 1)",
        };

        return (
            <List
                disablePadding
                className="text-color-white bg-sidebar-container"
                sx={{
                    height: "100vh",
                    '@supports (height: 100dvh)': {
                        height: "100dvh",
                    },
                }}
            >
            {
                groupedMenu.map((g, idx) => {
                    const subs = g.value.filter(s => s.isSubMenu).map((s, s_idx) => {
                        const isSelected = menuState?.selectedMenu?.groupName === s.groupName && menuState?.selectedMenu?.id === s.id;

                        return (
                            <ListItem
                                key={`submenu_${idx}_${s_idx}`}
                                disablePadding
                                sx={itemStyle}
                            >
                                <ListItemButton
                                    sx={itemButtonSubmenuStyle}
                                    onClick={() => {
                                        getSelectedMenu(s);

                                        if (s.id === "Logout" || !s.children?.length)
                                        {
                                            navigate(s.path);
                                            return;
                                        }

                                        if (isMobile)
                                            setMenuState({...menuState, type: "toggle", showSidebar: false});

                                        const defaultChild = s.children.filter(c => c.isDefault)[0];

                                        if (!defaultChild)
                                        {
                                            navigate(s.path);
                                            return;
                                        }

                                        navigate(`${s.path}${defaultChild.path}`);
                                    }}
                                >
                                {
                                    s.icon ?
                                    <s.icon sx={isSelected ? {...itemIconStyle, ...itemIconSelectedStyle} : itemIconStyle} fontSize="small" />
                                    :
                                    null
                                }
                                    <Typography sx={isSelected ? itemSelectedStyle : {}}>{s.title}</Typography>
                                </ListItemButton>
                            </ListItem>
                        );
                    });

                    const firstValue = g.value[0];
                    const GroupIcon = firstValue.group.icon;
                    const isGroupSelected = menuState?.selectedMenu?.groupName === firstValue.groupName;

                    return (
                        <ListItem
                            key={`menu_${idx}`}
                            disablePadding
                            sx={itemStyle}
                        >
                            <ListItemButton
                                sx={itemButtonStyle}
                                onClick={() => {
                                    getSelectedGroup(firstValue);

                                    if (!firstValue.isSubMenu)
                                    {
                                        getSelectedMenu(firstValue);

                                        if (isMobile)
                                            setMenuState({...menuState, type: "toggle", showSidebar: false});
                                        
                                        navigate(firstValue.path)
                                    }
                                }}
                            >
                                <GroupIcon sx={isGroupSelected ? {...itemIconStyle, ...itemIconSelectedStyle} : itemIconStyle} fontSize="small" />
                                <Typography sx={isGroupSelected ? itemSelectedStyle : {}}>{`${g.key === "Profil" && authState ? "" : g.key}${g.key === "Profil" && authState ? `${authState.username} (${authState.kode_divisi})` : ""}`}</Typography>
                            </ListItemButton>
                        {
                            subs.length > 0 &&
                            <Collapse
                                in={menuState?.selectedMenuGroup?.groupName === firstValue?.groupName}
                                timeout="auto"
                                unmountOnExit
                                sx={subItemContainerStyle}
                            >
                                <List disablePadding>
                                {
                                    subs
                                }
                                </List>
                            </Collapse>
                        }
                        </ListItem>
                    );
                })
            }
            </List>
        );
    }, [groupedMenu, getSelectedGroup, getSelectedMenu, menuState]);

    return (
        <>
            <Box
                component="nav"
                sx={{ width: { md: drawerWidth }, flexShrink: { md: 0 }, display: menuState?.showSidebar ? "block" : "none" }}
                aria-label="menu list"
                className="text-color-white bg-sidebar-menu-container"
            >
            {
                isMobile &&
                <Drawer
                    open={menuState?.showSidebar}
                    onClose={() => {
                        setMenuState({...menuState, type: "toggle", showSidebar: !menuState?.showSidebar});
                    }}
                    ModalProps={{
                        keepMounted: true, // Better open performance on mobile.
                        className: "bg-sidebar-menu-container",
                    }}
                    sx={{
                        display: "block",
                        '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
                        width: "100%",
                        height: "100vh",
                        '@supports (height: 100dvh)': {
                            height: "100dvh",
                        },
                    }}
                >
                {
                    mobileToolbar
                }
                {
                    drawerList(true)
                }
                </Drawer>
            }
            {
                !isMobile &&
                <Drawer
                    open={menuState?.showSidebar}
                    onClose={() => {
                        setMenuState({...menuState, type: "toggle", showSidebar: !menuState?.showSidebar});
                    }}
                    sx={{
                        display: "block",
                        '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
                    }}
                    variant="persistent"
                    ModalProps={{
                        className: "bg-sidebar-menu-container"
                    }}
                    transitionDuration={0}
                >
                {
                    toolbar
                }
                {
                    drawerList()
                }
                </Drawer>
            }
            </Box>
        </>
    );
}

export default Sidebar;
